/**
 * FindingsList Component
 * Displays compliance findings grouped by severity with fix guidance
 *
 * Part of Sprint Day 5-6: Compliance Dashboard UI
 */

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertCircle,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  ArrowRight,
  FileText,
  Upload,
  ExternalLink,
  BookOpen
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { Finding } from "@/hooks/useCompliance";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface FindingsListProps {
  findings: Finding[];
  onResolve?: (finding: Finding) => void;
  onUploadDocument?: (category: string) => void;
  loading?: boolean;
  emptyMessage?: string;
}

export function FindingsList({
  findings,
  onResolve,
  onUploadDocument,
  loading,
  emptyMessage = "No compliance issues found"
}: FindingsListProps) {
  const [expandedFindings, setExpandedFindings] = useState<Set<string>>(new Set());

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-muted rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!findings || findings.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-500" />
            <p className="font-medium">{emptyMessage}</p>
            <p className="text-sm mt-1">All requirements have been met</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Group findings by severity
  const criticalFindings = findings.filter(f => f.severity === 'critical');
  const warningFindings = findings.filter(f => f.severity === 'warning');
  const infoFindings = findings.filter(f => f.severity === 'info');

  const toggleFinding = (id: string) => {
    const newExpanded = new Set(expandedFindings);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedFindings(newExpanded);
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Compliance Findings
          </CardTitle>
          <div className="flex items-center gap-2">
            {criticalFindings.length > 0 && (
              <Badge variant="destructive">{criticalFindings.length} Critical</Badge>
            )}
            {warningFindings.length > 0 && (
              <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-300 dark:hover:bg-yellow-800">
                {warningFindings.length} Warning
              </Badge>
            )}
            {infoFindings.length > 0 && (
              <Badge variant="secondary">{infoFindings.length} Info</Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Critical Findings */}
        {criticalFindings.length > 0 && (
          <FindingsGroup
            title="Critical Issues"
            findings={criticalFindings}
            severity="critical"
            expandedFindings={expandedFindings}
            onToggle={toggleFinding}
            onResolve={onResolve}
            onUploadDocument={onUploadDocument}
          />
        )}

        {/* Warning Findings */}
        {warningFindings.length > 0 && (
          <FindingsGroup
            title="Warnings"
            findings={warningFindings}
            severity="warning"
            expandedFindings={expandedFindings}
            onToggle={toggleFinding}
            onResolve={onResolve}
            onUploadDocument={onUploadDocument}
          />
        )}

        {/* Info Findings */}
        {infoFindings.length > 0 && (
          <FindingsGroup
            title="Informational"
            findings={infoFindings}
            severity="info"
            expandedFindings={expandedFindings}
            onToggle={toggleFinding}
            onResolve={onResolve}
            onUploadDocument={onUploadDocument}
          />
        )}
      </CardContent>
    </Card>
  );
}

interface FindingsGroupProps {
  title: string;
  findings: Finding[];
  severity: 'critical' | 'warning' | 'info';
  expandedFindings: Set<string>;
  onToggle: (id: string) => void;
  onResolve?: (finding: Finding) => void;
  onUploadDocument?: (category: string) => void;
}

function FindingsGroup({
  title,
  findings,
  severity,
  expandedFindings,
  onToggle,
  onResolve,
  onUploadDocument
}: FindingsGroupProps) {
  const severityConfig = {
    critical: {
      borderColor: 'border-red-200 dark:border-red-800',
      bgColor: 'bg-red-50 dark:bg-red-950',
      iconColor: 'text-red-500 dark:text-red-400',
      Icon: AlertCircle
    },
    warning: {
      borderColor: 'border-yellow-200 dark:border-yellow-800',
      bgColor: 'bg-yellow-50 dark:bg-yellow-950',
      iconColor: 'text-yellow-500 dark:text-yellow-400',
      Icon: AlertTriangle
    },
    info: {
      borderColor: 'border-blue-200 dark:border-blue-800',
      bgColor: 'bg-blue-50 dark:bg-blue-950',
      iconColor: 'text-blue-500 dark:text-blue-400',
      Icon: Info
    }
  };

  const config = severityConfig[severity];
  const Icon = config.Icon;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Icon className={cn("h-4 w-4", config.iconColor)} />
        {title}
      </div>
      <div className="space-y-2">
        {findings.map((finding, index) => {
          // Use a unique ID that includes the index to prevent multiple findings from sharing state
          // Include affectedField to differentiate "MISSING_DOCUMENT" findings for different document types
          const findingId = `${finding.ruleCode || 'finding'}-${finding.affectedField || ''}-${index}`;
          const isExpanded = expandedFindings.has(findingId);

          return (
            <FindingCard
              key={findingId}
              finding={finding}
              severity={severity}
              isExpanded={isExpanded}
              onToggle={() => onToggle(findingId)}
              onResolve={onResolve ? () => onResolve(finding) : undefined}
              onUploadDocument={onUploadDocument}
            />
          );
        })}
      </div>
    </div>
  );
}

interface FindingCardProps {
  finding: Finding;
  severity: 'critical' | 'warning' | 'info';
  isExpanded: boolean;
  onToggle: () => void;
  onResolve?: () => void;
  onUploadDocument?: (category: string) => void;
}

function FindingCard({
  finding,
  severity,
  isExpanded,
  onToggle,
  onResolve,
  onUploadDocument
}: FindingCardProps) {
  const severityConfig = {
    critical: {
      borderColor: 'border-red-200 dark:border-red-800',
      bgColor: 'bg-red-50 dark:bg-red-950',
      hoverBg: 'hover:bg-red-100 dark:hover:bg-red-900',
      iconColor: 'text-red-500 dark:text-red-400',
      Icon: AlertCircle
    },
    warning: {
      borderColor: 'border-yellow-200 dark:border-yellow-800',
      bgColor: 'bg-yellow-50 dark:bg-yellow-950',
      hoverBg: 'hover:bg-yellow-100 dark:hover:bg-yellow-900',
      iconColor: 'text-yellow-500 dark:text-yellow-400',
      Icon: AlertTriangle
    },
    info: {
      borderColor: 'border-blue-200 dark:border-blue-800',
      bgColor: 'bg-blue-50 dark:bg-blue-950',
      hoverBg: 'hover:bg-blue-100 dark:hover:bg-blue-900',
      iconColor: 'text-blue-500 dark:text-blue-400',
      Icon: Info
    }
  };

  const config = severityConfig[severity];
  const Icon = config.Icon;

  // Determine if this finding can be fixed by uploading a document
  const canUploadToFix = finding.ruleCode?.startsWith('MISSING_') ||
    finding.description?.toLowerCase().includes('missing') ||
    finding.suggestedFix?.toLowerCase().includes('upload');

  // Extract document category from the rule code if available
  const getDocCategory = () => {
    if (finding.ruleCode?.startsWith('MISSING_')) {
      return finding.ruleCode.replace('MISSING_', '').toLowerCase();
    }
    return finding.affectedField || 'document';
  };

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <div className={cn(
        "border rounded-lg overflow-hidden",
        config.borderColor,
        config.bgColor
      )}>
        <CollapsibleTrigger asChild>
          <button className={cn(
            "w-full flex items-center gap-3 p-3 text-left transition-colors",
            config.hoverBg
          )}>
            <Icon className={cn("h-5 w-5 shrink-0", config.iconColor)} />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{finding.title}</p>
              <p className="text-xs text-muted-foreground line-clamp-3">
                {finding.description}
              </p>
            </div>
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0" />
            )}
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t border-border bg-background p-3 space-y-3">
            {/* Full Description */}
            {finding.description && (
              <div className="p-2 bg-muted/50 rounded">
                <p className="text-sm">{finding.description}</p>
              </div>
            )}

            {/* Expected vs Actual */}
            {(finding.expectedValue || finding.actualValue) && (
              <div className="grid grid-cols-2 gap-3 text-sm">
                {finding.expectedValue && (
                  <div className="p-2 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800">
                    <p className="text-xs text-green-600 dark:text-green-400 font-medium mb-1">Expected</p>
                    <p className="text-green-800 dark:text-green-200">{finding.expectedValue}</p>
                  </div>
                )}
                {finding.actualValue && (
                  <div className="p-2 bg-red-50 dark:bg-red-950 rounded border border-red-200 dark:border-red-800">
                    <p className="text-xs text-red-600 dark:text-red-400 font-medium mb-1">Actual</p>
                    <p className="text-red-800 dark:text-red-200">{finding.actualValue}</p>
                  </div>
                )}
              </div>
            )}

            {/* Suggested Fix */}
            {finding.suggestedFix && (
              <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded border border-blue-200 dark:border-blue-800">
                <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1 flex items-center gap-1">
                  <ArrowRight className="h-3 w-3" />
                  How to Fix
                </p>
                <p className="text-sm text-blue-800 dark:text-blue-200">{finding.suggestedFix}</p>
              </div>
            )}

            {/* Code Citations */}
            {finding.citations && finding.citations.length > 0 && (
              <div className="p-2 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700">
                <p className="text-xs text-slate-600 dark:text-slate-400 font-medium mb-2 flex items-center gap-1">
                  <BookOpen className="h-3 w-3" />
                  Code References
                </p>
                <div className="space-y-1.5">
                  {finding.citations.map((citation, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-700 dark:text-slate-300">{citation.label}</span>
                        {citation.editionYear && (
                          <span className="text-slate-500 dark:text-slate-400">({citation.editionYear})</span>
                        )}
                      </div>
                      {citation.sourceUrl && (
                        <a
                          href={citation.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                        >
                          View <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
                {finding.citations[0]?.summary && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 italic">
                    {finding.citations[0].summary}
                  </p>
                )}
              </div>
            )}

            {/* Affected Field */}
            {finding.affectedField && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <FileText className="h-3 w-3" />
                Affected: {finding.affectedField}
              </div>
            )}

            {/* Rule Code */}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Rule: {finding.ruleCode}</span>
              {finding.status === 'resolved' && (
                <Badge variant="outline" className="text-green-600 border-green-300">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Resolved
                </Badge>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 pt-2 border-t">
              {canUploadToFix && onUploadDocument && (
                <Button
                  size="sm"
                  onClick={() => onUploadDocument(getDocCategory())}
                >
                  <Upload className="h-3.5 w-3.5 mr-1" />
                  Upload Document
                </Button>
              )}
              {onResolve && finding.status !== 'resolved' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onResolve}
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                  Mark Resolved
                </Button>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

export default FindingsList;
